import { useCallback, useState } from "react";

import { useMutation, useQueryClient } from "react-query";
import { SavedSearchesClient } from "services/motorpoint/user-context-api";
import type {
    RenameSearchParams,
    SavedSearchWithMeta,
    UserContext,
    UserContextId,
} from "services/motorpoint/user-context-api/types";

import { QUERY_KEY } from "./useSavedSearches";

const savedSearchesClient = new SavedSearchesClient({
    useEnv: true,
});

export const useSavedSearchesApi = ({
    userContextId,
    page,
    order,
    lastItemFallback,
}: UserContextId & { page: number; order: string } & {
    lastItemFallback: (reset?: boolean) => void;
}) => {
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const onSaveSuccess = (response: SavedSearchWithMeta[]) => {
        queryClient.setQueryData([QUERY_KEY, page, order], (oldData: any) => {
            return {
                ...oldData,
                searches: [...oldData.searches, ...response],
            };
        });
    };

    const onDeleteAllSuccess = () => {
        queryClient.setQueryData([QUERY_KEY, page, order], () => ({
            searches: [],
        }));

        lastItemFallback(true);

        queryClient.setQueryData("userContext", (oldData: UserContext | undefined) => {
            return {
                ...oldData,
                savedDataMeta: {
                    ...oldData?.savedDataMeta,
                    savedSearchesCount: 0,
                },
            } as UserContext;
        });
    };

    const onRenameSuccess = (response: any) => {
        queryClient.setQueryData([QUERY_KEY, page, order], (oldData: any) => {
            const searches = oldData?.searches.map((search: SavedSearchWithMeta) =>
                search.id === response.id ? response : search
            );
            return {
                ...oldData,
                searches,
            };
        });
    };

    const onDeleteSuccess = (
        _response: any,
        requestParams: UserContextId & { searchId: string }
    ) => {
        queryClient.setQueryData([QUERY_KEY, page, order], (oldData: any) => {
            const searches = oldData?.searches.filter(
                (search: SavedSearchWithMeta) => search.id !== requestParams.searchId
            );

            if (!searches.length) lastItemFallback();

            return {
                ...oldData,
                searches,
            };
        });

        queryClient.setQueryData(["userContext"], (oldData: any) => {
            return Object.assign({}, oldData, {
                savedDataMeta: {
                    ...oldData?.savedDataMeta,
                    savedSearchesCount: oldData.savedDataMeta.savedSearchesCount - 1,
                },
            });
        });

        queryClient.invalidateQueries(["userContext"]);
    };

    const saveSearchesBind = savedSearchesClient.saveSearches.bind(savedSearchesClient);
    const deleteSearchBind = savedSearchesClient.deleteSearchById.bind(savedSearchesClient);
    const deleteAllBind = savedSearchesClient.deleteAll.bind(savedSearchesClient);
    const renameSearchBind = savedSearchesClient.renameSearch.bind(savedSearchesClient);

    const saveSearchesMutation = useMutation({
        mutationFn: saveSearchesBind,
        onSuccess: onSaveSuccess,
    });

    const deleteSearchMutation = useMutation({
        mutationFn: deleteSearchBind,
        onSuccess: onDeleteSuccess,
    });

    const deleteAllMutation = useMutation({
        mutationFn: deleteAllBind,
        onSuccess: onDeleteAllSuccess,
    });

    const renameSearchMutation = useMutation({
        mutationFn: renameSearchBind,
        onSuccess: onRenameSuccess,
    });

    const saveSearches = useCallback(
        async (stockNumber: any) => {
            try {
                const params: any = {
                    userContextId,
                    stockNumber,
                };
                const data = await saveSearchesMutation.mutateAsync(params);

                return data;
            } catch (error) {
                setError("Could not save searches");
            }
        },
        // eslint-disable-next-line
        [saveSearchesMutation.mutateAsync]
    );

    const deleteSearch = useCallback(
        async (searchId: string) => {
            try {
                const params: UserContextId & { searchId: string } = {
                    userContextId,
                    searchId,
                };

                const data = await deleteSearchMutation.mutateAsync(params);

                return data;
            } catch (error) {
                setError("Could not delete saved search");
            }
        },
        // eslint-disable-next-line
        [deleteSearchMutation.mutateAsync]
    );

    const deleteAll = useCallback(
        async () => {
            try {
                const data = await deleteAllMutation.mutateAsync({ userContextId });

                return data;
            } catch (error) {
                setError("Could not delete all saved searches");
            }
        },
        // eslint-disable-next-line
        [deleteAllMutation.mutateAsync]
    );

    const renameSearch = useCallback(
        async (params: Omit<RenameSearchParams, "userContextId">) => {
            try {
                const data = await renameSearchMutation.mutateAsync({ userContextId, ...params });

                return data;
            } catch (error) {
                setError("Could not rename saved search");
            }
        },
        // eslint-disable-next-line
        [renameSearchMutation.mutateAsync]
    );

    return {
        error,
        saveSearches,
        deleteSearch,
        deleteAll,
        renameSearch,
    };
};
